import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  Sms as SmsIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const SmsConfig = () => {
  const [loading, setLoading] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [configData, setConfigData] = useState({
    provider: 'hostpinnacle',
    api_key: '',
    username: '',
    password: '',
    sender_id: '',
    environment: 'production',
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/api/config/sms');
      if (response.data) {
        setConfigData((prev) => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch SMS config:', error);
    }
  };

  const handleChange = (e) => {
    setConfigData({
      ...configData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/api/config/sms', configData);
      toast.success('SMS configuration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMS configuration');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sendTestSms = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number to test');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/config/sms/test', {
        phone_number: testPhone,
      });
      toast.success('Test SMS sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const isHostPinnacle = configData.provider === 'hostpinnacle';

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <SmsIcon color="primary" />
        <Typography variant="h6">SMS Configuration</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Provider Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>SMS Provider</InputLabel>
              <Select
                name="provider"
                value={configData.provider}
                onChange={handleChange}
                label="SMS Provider"
              >
<MenuItem value="hostpinnacle">Comsomanet</MenuItem>
                <MenuItem value="africastalking">Africa's Talking</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Environment - only for Africa's Talking */}
          {!isHostPinnacle && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Environment</InputLabel>
                <Select
                  name="environment"
                  value={configData.environment}
                  onChange={handleChange}
                  label="Environment"
                >
                  <MenuItem value="sandbox">Sandbox (Testing)</MenuItem>
                  <MenuItem value="production">Production (Live)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Username / User ID */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={isHostPinnacle ? 'User ID' : 'API Username'}
              name="username"
              value={configData.username}
              onChange={handleChange}
              helperText={isHostPinnacle
? 'Your Comsomanet user ID'
                : "Your Africa's Talking username"
              }
            />
          </Grid>

          {/* API Key */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="API Key"
              name="api_key"
              type="password"
              value={configData.api_key}
              onChange={handleChange}
              required
              helperText={isHostPinnacle
? 'Your Comsomanet API key'
                : "Your Africa's Talking API key"
              }
            />
          </Grid>

          {/* Password - only for Comsomanet */}
          {isHostPinnacle && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={configData.password || ''}
                onChange={handleChange}
helperText="Your Comsomanet password (optional if using API key)"
              />
            </Grid>
          )}

          {/* Sender ID */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Sender ID"
              name="sender_id"
              value={configData.sender_id}
              onChange={handleChange}
              helperText={isHostPinnacle
? 'Your registered sender ID on Comsomanet'
                : 'Custom sender ID (optional, max 11 characters)'
              }
              inputProps={{ maxLength: 11 }}
            />
          </Grid>

          {/* Test Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Test Configuration
            </Typography>
            <TextField
              fullWidth
              label="Test Phone Number"
              placeholder="254712345678"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              helperText={isHostPinnacle
                ? 'Enter phone number with country code (e.g., 254712345678)'
                : 'Enter phone number with country code (e.g., +254712345678)'
              }
              sx={{ mt: 2 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                Save Configuration
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={sendTestSms}
                disabled={loading}
              >
                Send Test SMS
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Box sx={{ mt: 3 }}>
        {isHostPinnacle ? (
          <Alert severity="info">
<strong>Comsomanet SMS Setup:</strong>
            <br />
            1. Log in to your Comsomanet SMS portal
            <br />
            2. Get your API key from the dashboard
            <br />
            3. Register your Sender ID
            <br />
            4. Enter your credentials above and save
            <br />
            5. Use "Send Test SMS" to verify
          </Alert>
        ) : (
          <Alert severity="info">
            <strong>Africa's Talking SMS Setup:</strong>
            <br />
            1. Sign up at https://africastalking.com
            <br />
            2. Get your API key from the dashboard
            <br />
            3. For production, register a sender ID
            <br />
            4. Use sandbox for testing (free credits available)
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default SmsConfig;
